import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/**
 * Custom hook for real-time carpool tracking via Socket.IO
 * 
 * @param {string} rideId - The carpool request ID to track
 * @param {string} userType - 'driver' or 'rider'
 * @param {string} userId - The current user's ID
 * @returns {Object} Socket state and methods
 */
export function useSocketTracking(rideId, userType, userId) {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [rideStatus, setRideStatus] = useState(null);
  const [eta, setEta] = useState(null);
  const [error, setError] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);

  // Initialize socket connection
  useEffect(() => {
    if (!rideId || !userId) return;

    // The backend's connect handler rejects any socket without a valid JWT in
    // auth.token, so we must pass the stored token (not just the user id).
    const token = localStorage.getItem('token');
    if (!token) {
      setError('You must be signed in to track this ride.');
      return;
    }

    // Create socket connection with auth.
    // Polling-first (then upgrade to WebSocket) matches the working mobile
    // client and is far more reliable behind the production proxy, where a
    // direct WebSocket upgrade is often blocked and v4 won't fall back cleanly
    // when 'websocket' is listed first.
    socketRef.current = io(SOCKET_URL, {
      auth: { token, user_id: userId },
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    const socket = socketRef.current;

    // Connection events
    socket.on('connect', () => {
      console.log('[Socket.IO] Connected:', socket.id);
      setIsConnected(true);
      setError(null);
      
      // Join the ride room
      socket.emit('join_ride', { ride_id: rideId, user_type: userType });
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket.IO] Disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[Socket.IO] Connection error:', err?.message, err);
      setError(`Connection failed: ${err?.message || 'unknown error'}. Retrying...`);
    });

    // Ride events
    socket.on('joined_ride', (data) => {
      console.log('[Socket.IO] Joined ride:', data);
    });

    socket.on('ride_state', (state) => {
      console.log('[Socket.IO] Ride state:', state);
      if (state.driver_location) {
        setDriverLocation(state.driver_location);
      }
      if (state.status) {
        setRideStatus(state.status);
      }
      if (state.driver) {
        setDriverInfo(state.driver);
      }
    });

    socket.on('ride_started', (data) => {
      console.log('[Socket.IO] Ride started:', data);
      setRideStatus('started');
      if (data.driver) {
        setDriverInfo(data.driver);
      }
    });

    socket.on('driver_location', (data) => {
      console.log('[Socket.IO] Driver location update:', data);
      setDriverLocation({
        latitude: data.latitude,
        longitude: data.longitude,
        heading: data.heading,
        speed: data.speed,
        timestamp: data.timestamp
      });
    });

    socket.on('eta_update', (data) => {
      console.log('[Socket.IO] ETA update:', data);
      setEta({
        minutes: data.eta_minutes,
        distance: data.distance_meters
      });
    });

    socket.on('driver_arrived', (data) => {
      console.log('[Socket.IO] Driver arrived:', data);
      setRideStatus('arrived');
    });

    socket.on('rider_picked_up', (data) => {
      console.log('[Socket.IO] Rider picked up:', data);
      setRideStatus('picked_up');
    });

    socket.on('ride_completed', (data) => {
      console.log('[Socket.IO] Ride completed:', data);
      setRideStatus('completed');
    });

    socket.on('error', (error) => {
      console.error('[Socket.IO] Error:', error);
      setError(error.message);
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.emit('leave_ride', { ride_id: rideId });
        socket.disconnect();
      }
    };
  }, [rideId, userId, userType]);

  // Send location update (for driver)
  const sendLocationUpdate = useCallback((latitude, longitude, heading = 0, speed = 0) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('location_update', {
        ride_id: rideId,
        latitude,
        longitude,
        heading,
        speed
      });
    }
  }, [rideId, isConnected]);

  // Send ETA update
  const sendEtaUpdate = useCallback((etaMinutes, distanceMeters) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('update_eta', {
        ride_id: rideId,
        eta_minutes: etaMinutes,
        distance_meters: distanceMeters
      });
    }
  }, [rideId, isConnected]);

  return {
    isConnected,
    driverLocation,
    rideStatus,
    eta,
    error,
    driverInfo,
    sendLocationUpdate,
    sendEtaUpdate,
    socket: socketRef.current
  };
}

/**
 * Hook for driver to continuously track and send their location
 * 
 * @param {string} rideId - The carpool request ID
 * @param {boolean} isTracking - Whether to actively track location
 * @param {function} onLocationUpdate - Callback when location updates
 * @returns {Object} Geolocation state and methods
 */
export function useDriverLocation(rideId, isTracking, onLocationUpdate) {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!isTracking || !rideId) {
      // Stop watching if not tracking
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        setIsWatching(false);
      }
      return;
    }

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    // Start watching position
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000 // Allow cached position up to 5 seconds old
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          heading: position.coords.heading || 0,
          speed: position.coords.speed || 0,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        setCurrentLocation(location);
        setLocationError(null);
        setIsWatching(true);
        
        // Call the callback with new location
        if (onLocationUpdate) {
          onLocationUpdate(location);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('An unknown error occurred.');
        }
      },
      options
    );

    // Cleanup
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        setIsWatching(false);
      }
    };
  }, [isTracking, rideId, onLocationUpdate]);

  return {
    currentLocation,
    locationError,
    isWatching
  };
}

export default useSocketTracking;
